import { AfterViewInit, Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FormulaireMouvementService } from '@services/formulaire-mouvement/formulaire-mouvement.service';
import { GabaritService } from '@services/gabarit/gabarit.service';
import { GlobalStoreService } from '@services/global-store/global-store.service';
import { NavigationService } from '@services/navigation/navigation.service';
import { ParticulierMouvement } from 'src/app/api/model/particulier-mouvement.model';
import { FormulaireCoordonneesMouvementCourrielBuilder } from '../../models/formulaire/formulaire-coordonnees-mouvement-courriel-builder.model';
import { FormulaireCoordonneesMouvementCourrielModel } from 'src/app/models/formulaire/formulaire-coordonnees-mouvement-courriel.model';
import { GestionTitreService } from '@services/navigation/gestion-titre.service';
import { AccessibiliteService } from '@services/accessibilite.service';
import { CybermetrieCoordonneesService } from '@services/cybermetrie/cybermetrie.service';
import { CybermetrieEnum } from '../../constantes/cybermetrie.enum';
import { ContexteModificationEnum } from '../../constantes/contexte-modification.enum';

@Component({
  selector: 'app-modifier-courriel',
  templateUrl: './modifier-courriel.component.html',
  standalone: false,
})
export class ModifierCourrielComponent implements OnInit, AfterViewInit {
  isAppMobile?: boolean;
  coordonneesParticulierMouvement: ParticulierMouvement | null = null;
  formGroup!: FormGroup<FormulaireCoordonneesMouvementCourrielModel>;
  champEnErreur: string = 'adresse courriel';

  @ViewChild('courriel', { static: true }) courrielInput!: HTMLDsdInputElement;

  private readonly globalStoreService = inject(GlobalStoreService);
  private readonly formulaireMouvementService = inject(FormulaireMouvementService);
  private readonly gabaritService = inject(GabaritService);
  private readonly navigationService = inject(NavigationService);
  private readonly gestionTitreService = inject(GestionTitreService);
  private readonly accessibiliteService = inject(AccessibiliteService);
  private readonly cybermetrieCoordonneesService = inject(CybermetrieCoordonneesService);

  ngOnInit(): void {
    this.isAppMobile = this.gabaritService.isAppMobile();

    const formExistant = this.formulaireMouvementService.getFormCourriel();
    if (formExistant?.get('courriel')?.value) {
      this.formGroup = formExistant;
    } else {
      this.globalStoreService.getCoordonneesMouvement$().subscribe({
        next: (data) => {
          this.coordonneesParticulierMouvement = data;
          const courriel = this.coordonneesParticulierMouvement?.courriel?.adresseCourriel ?? '';
          const courrielExistant = !!courriel;
          this.formGroup = FormulaireCoordonneesMouvementCourrielBuilder.buildCourriel();
          this.formGroup.patchValue({ courriel, courrielExistant });
          this.formulaireMouvementService.setFormCourriel(this.formGroup);
          this.cybermetrieCoordonneesService.transactionDebuter(
            this.formGroup.getRawValue().courrielExistant
              ? CybermetrieEnum.LABEL_MODIFIER_COURRIEL
              : CybermetrieEnum.LABEL_MODIFIER_COURRIEL_AJOUT,
          );
        },
      });
    }
    this.setTitreOnglet();
    this.globalStoreService.saveContexteModification(ContexteModificationEnum.COURRIEL);
  }

  setTitreOnglet(): void {
    if (this.estCourrielExistant) {
      this.gestionTitreService.addTitreOnglet('titreOnglet.modifierCourriel');
    } else {
      this.gestionTitreService.addTitreOnglet('titreOnglet.ajouterCourriel');
    }
  }

  ngAfterViewInit(): void {
    this.accessibiliteService.setFocus('#titreCourriel');
    this.courrielInput.registerCustomValidate(
      (value: string) => {
        return this.validerInput(value, 'courriel', 'courrielInvalide');
      },
      { errorKey: 'courrielInvalide' },
    );
  }

  get estCourrielExistant(): boolean {
    return this.formGroup.getRawValue().courrielExistant;
  }

  validerInput(value: string, nomFormControl: string, codeErreur: string) {
    const control = this.formGroup.get(nomFormControl);
    if (!control) return false;
    control.setValue(value);
    return !control.hasError(codeErreur);
  }

  naviguerVersValidation(): void {
    if (this.formGroup.valid) {
      this.navigationService.naviguerVersValidationCourriel();
    } else {
      this.cybermetrieCoordonneesService.transactionErreurUtilisateur(
        CybermetrieEnum.LABEL_MODIFIER_COURRIEL,
        [this.champEnErreur],
      );
    }
  }

  annuler(): void {
    this.globalStoreService.saveEtatAfficherModaleAnnuler(true);
  }
}
